import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError("Invalid verification link");
        setLoading(false);
        return;
      }

      try {
        await axios.get(`${API}/auth/verify-email/${token}`);
        setVerified(true);
        toast.success("Email verified successfully!");
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate("/dashboard");
        }, 3000);
      } catch (err) {
        setError(err.response?.data?.detail || "Verification failed");
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center" style={{ fontFamily: 'Inter, sans-serif' }}>
        <Card className="w-full max-w-md border-[#e5e5e0]">
          <CardContent className="pt-8 text-center">
            <Loader2 className="h-16 w-16 mx-auto text-[#1a4d2e] animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-[#1a1a1a]">Verifying your email...</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
        <Card className="w-full max-w-md border-[#e5e5e0]">
          <CardContent className="pt-8 text-center">
            <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-[#1a1a1a] mb-2">Verification Failed</h2>
            <p className="text-[#666] mb-6">{error}</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-[#1a4d2e] hover:bg-[#2d6a4f]">
                  Go to Login
                </Button>
              </Link>
              <p className="text-sm text-[#999]">
                Need a new verification link?{" "}
                <Link to="/login" className="text-[#1a4d2e] underline">
                  Log in and request one
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Card className="w-full max-w-md border-[#e5e5e0]">
        <CardContent className="pt-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-[#1a4d2e] mb-2">Email Verified!</h2>
          <p className="text-[#666] mb-6">
            Your email has been verified successfully. You'll now receive lead notifications and other important updates.
          </p>
          <div className="bg-[#f8f8f5] rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-[#1a4d2e]">
              <Mail className="h-5 w-5" />
              <span className="font-medium">Notifications Activated</span>
            </div>
          </div>
          <p className="text-sm text-[#999] mb-4">Redirecting to dashboard...</p>
          <Link to="/dashboard">
            <Button className="bg-[#1a4d2e] hover:bg-[#2d6a4f]">
              Go to Dashboard Now
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
